import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './HomePageCMS.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface ValueCard {
  icon: string;
  title: string;
  title_bn: string;
  description: string;
  description_bn: string;
}

interface AboutContent {
  hero_title: string;
  hero_title_bn: string;
  hero_subtitle: string;
  hero_subtitle_bn: string;
  story_title: string;
  story_title_bn: string;
  story_content: string;
  story_content_bn: string;
  values: ValueCard[];
  mission_title: string;
  mission_title_bn: string;
  mission_content: string;
  mission_content_bn: string;
  cta_title: string;
  cta_title_bn: string;
  cta_subtitle: string;
  cta_subtitle_bn: string;
}

const EMPTY: AboutContent = {
  hero_title: '', hero_title_bn: '', hero_subtitle: '', hero_subtitle_bn: '',
  story_title: '', story_title_bn: '', story_content: '', story_content_bn: '',
  values: [],
  mission_title: '', mission_title_bn: '', mission_content: '', mission_content_bn: '',
  cta_title: '', cta_title_bn: '', cta_subtitle: '', cta_subtitle_bn: '',
};

const AboutPageCMS: React.FC = () => {
  const [content, setContent] = useState<AboutContent>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/about/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setContent(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContent(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleValueChange = (i: number, field: keyof ValueCard, value: string) => {
    const v = [...content.values];
    v[i] = { ...v[i], [field]: value };
    setContent(prev => ({ ...prev, values: v }));
  };

  const addValue = () => setContent(prev => ({
    ...prev, values: [...prev.values, { icon: '\u2B50', title: '', title_bn: '', description: '', description_bn: '' }],
  }));

  const removeValue = (i: number) => setContent(prev => ({
    ...prev, values: prev.values.filter((_, idx) => idx !== i),
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/about/1/`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (res.ok) {
        setContent(await res.json());
        Swal.fire({ icon: 'success', title: 'Saved!', text: 'About page updated', timer: 1500, showConfirmButton: false });
      } else throw new Error();
    } catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save' }); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="homepage-cms-container">
      <div className="cms-card"><div className="card-content">
        <div className="cms-skeleton" style={{ height: 40 }} />
        <div className="cms-skeleton" style={{ height: 40, marginTop: 12 }} />
      </div></div>
    </div>
  );

  return (
    <div className="homepage-cms-container">
      <form onSubmit={handleSubmit}>

        {/* Hero */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left">
              <div className="card-icon icon-blue">{'\uD83D\uDCC4'}</div>
              <h3>Hero Section</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Hero Title (English)</label><input className="form-input" name="hero_title" value={content.hero_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Title (বল)</label><input className="form-input" name="hero_title_bn" value={content.hero_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginTop: 8 }}>
              <div className="cms-form-group"><label>Hero Subtitle (English)</label><input className="form-input" name="hero_subtitle" value={content.hero_subtitle} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Subtitle (বল)</label><input className="form-input" name="hero_subtitle_bn" value={content.hero_subtitle_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        {/* Our Story */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left">
              <div className="card-icon icon-blue">{'\uD83D\uDCD6'}</div>
              <h3>Our Story</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 12 }}>
              <div className="cms-form-group"><label>Section Title (English)</label><input className="form-input" name="story_title" value={content.story_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Section Title (বল)</label><input className="form-input" name="story_title_bn" value={content.story_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Story Content (English)</label><textarea className="form-input" name="story_content" value={content.story_content} onChange={handleChange} rows={5} style={{ resize: 'vertical' }} /></div>
              <div className="cms-form-group"><label>Story Content (বল)</label><textarea className="form-input" name="story_content_bn" value={content.story_content_bn} onChange={handleChange} rows={5} style={{ resize: 'vertical' }} /></div>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left">
              <div className="card-icon icon-blue">{'\uD83C\uDFC6'}</div>
              <h3>Our Values</h3>
            </div>
            <button type="button" className="btn-add-feature" onClick={addValue}>+ Add Value</button>
          </div>
          <div className="card-content">
            {content.values.length === 0 && (
              <div className="empty-state"><p>No values added yet. Click "Add Value" to start.</p></div>
            )}
            {content.values.map((v, i) => (
              <div key={i} className="feature-item-card" style={{ marginBottom: 12 }}>
                <div className="feature-header">
                  <span className="feature-count">#{i + 1}</span>
                  <button type="button" className="btn-remove-feature" onClick={() => removeValue(i)}>{'\u2715'}</button>
                </div>
                <div className="cms-form-group" style={{ marginBottom: 8 }}>
                  <label>Icon (emoji)</label>
                  <input className="form-input" value={v.icon} onChange={e => handleValueChange(i, 'icon', e.target.value)} style={{ maxWidth: 120 }} />
                </div>
                <div className="cms-grid-2" style={{ marginBottom: 8 }}>
                  <div className="cms-form-group"><label>Title (English)</label><input className="form-input" value={v.title} onChange={e => handleValueChange(i, 'title', e.target.value)} /></div>
                  <div className="cms-form-group"><label>Title (বল)</label><input className="form-input" value={v.title_bn} onChange={e => handleValueChange(i, 'title_bn', e.target.value)} /></div>
                </div>
                <div className="cms-grid-2">
                  <div className="cms-form-group"><label>Description (English)</label><input className="form-input" value={v.description} onChange={e => handleValueChange(i, 'description', e.target.value)} /></div>
                  <div className="cms-form-group"><label>Description (বল)</label><input className="form-input" value={v.description_bn} onChange={e => handleValueChange(i, 'description_bn', e.target.value)} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mission */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left">
              <div className="card-icon icon-blue">{'\uD83C\uDFAF'}</div>
              <h3>Mission</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 12 }}>
              <div className="cms-form-group"><label>Mission Title (English)</label><input className="form-input" name="mission_title" value={content.mission_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Mission Title (বল)</label><input className="form-input" name="mission_title_bn" value={content.mission_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Mission Content (English)</label><textarea className="form-input" name="mission_content" value={content.mission_content} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
              <div className="cms-form-group"><label>Mission Content (বল)</label><textarea className="form-input" name="mission_content_bn" value={content.mission_content_bn} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left">
              <div className="card-icon icon-blue">{'\uD83D\uDCE2'}</div>
              <h3>Call to Action</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>CTA Title (English)</label><input className="form-input" name="cta_title" value={content.cta_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>CTA Title (বল)</label><input className="form-input" name="cta_title_bn" value={content.cta_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>CTA Subtitle (English)</label><input className="form-input" name="cta_subtitle" value={content.cta_subtitle} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>CTA Subtitle (বল)</label><input className="form-input" name="cta_subtitle_bn" value={content.cta_subtitle_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        <div className="cms-page-actions">
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? 'Saving...' : '\uD83D\uDCBE Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AboutPageCMS;
